/**
 * 兑换码生码页。
 *
 * 功能：
 *  - 批量生码（数量 / 批次 / 备注 / 有效期）→ 结果可一键复制清单
 *  - 列表看核销情况（状态 / 批次过滤），批次用于多渠道归因
 *
 * 权限：
 *  - redeem_code:read — 全页（列表返回所有批次的明文未用码，所以单独一个点位，不跟 voucher:read 走）
 *  - voucher:grant    — 生码表单（兑换码本质是"发免单券"的入口，生码复用发券权限点）
 */
import { useMemo, useState } from 'react';
import {
  Card, Table, Form, Input, InputNumber, Button, Space, Tag, Modal,
  Alert, message, Select, DatePicker, Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { type Dayjs } from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '../../components/PermissionGate';
import { PERMISSIONS } from '../../contracts/permissions';
import {
  listRedeemCodes, generateRedeemCodes, getRedeemFunnel, type RedeemFunnelRow,
  type RedeemCodeDto, type ListRedeemCodesFilter,
} from '../../api/redeem-codes';

function formatTs(ms: number | null): string {
  if (ms == null) return '-';
  return new Date(ms).toLocaleString('zh-CN');
}

function RedeemCodesPageInner() {
  const qc = useQueryClient();
  const [genForm] = Form.useForm<{ count: number; batch?: string; note?: string; expireAt?: Dayjs }>();
  const [filterForm] = Form.useForm<{ status?: 'unused' | 'used'; batch?: string }>();
  const [filters, setFilters] = useState<ListRedeemCodesFilter>({});
  const [resultCodes, setResultCodes] = useState<string[] | null>(null);

  const listQuery = useQuery({
    queryKey: ['redeem-codes', filters],
    queryFn: () => listRedeemCodes(filters),
  });

  const funnelQuery = useQuery({
    queryKey: ['redeem-codes-funnel'],
    queryFn: getRedeemFunnel,
  });
  const threshold = funnelQuery.data?.chapterThreshold ?? 3;
  const funnelColumns: ColumnsType<RedeemFunnelRow> = [
    {
      title: '批次 / 渠道', dataIndex: 'batch', key: 'batch',
      render: (b: string | null) => (
        <Space size={4}>
          {b ?? '（未标批次）'}
          {b && b.toLowerCase().startsWith('internal') && <Tag color="orange">内部测试</Tag>}
        </Space>
      ),
    },
    { title: '码总数', dataIndex: 'totalCodes', key: 'totalCodes' },
    { title: '已分配', dataIndex: 'assigned', key: 'assigned' },
    { title: '剩余可发', dataIndex: 'remaining', key: 'remaining' },
    { title: '已核销', dataIndex: 'redeemed', key: 'redeemed' },
    { title: '开始讲述(人)', dataIndex: 'narratedUsers', key: 'narratedUsers' },
    { title: '累计讲述(分钟)', dataIndex: 'narratedMinutes', key: 'narratedMinutes' },
    { title: `章节≥${threshold}(人)`, dataIndex: 'chapterUsers', key: 'chapterUsers' },
    { title: '已付款(人)', dataIndex: 'paidUsers', key: 'paidUsers' },
  ];

  const genMutation = useMutation({
    mutationFn: generateRedeemCodes,
    onSuccess: (data) => {
      setResultCodes(data.codes);
      message.success(`成功生成 ${data.codes.length} 个兑换码`);
      void qc.invalidateQueries({ queryKey: ['redeem-codes'] });
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (e as Error)?.message ??
        '生码失败';
      message.error(msg);
    },
  });

  function handleGenerate() {
    void genForm.validateFields().then((v) => {
      genMutation.mutate({
        count: v.count,
        batch: v.batch?.trim() || undefined,
        note: v.note?.trim() || undefined,
        expireAt: v.expireAt ? v.expireAt.valueOf() : undefined,
      });
    });
  }

  const columns: ColumnsType<RedeemCodeDto> = [
    {
      title: '兑换码', dataIndex: 'code', key: 'code',
      render: (c: string) => <Typography.Text copyable code>{c}</Typography.Text>,
    },
    {
      title: '状态', dataIndex: 'status', key: 'status',
      render: (s: RedeemCodeDto['status']) => (
        <Tag color={s === 'unused' ? 'green' : 'blue'}>{s === 'unused' ? '未使用' : '已使用'}</Tag>
      ),
    },
    { title: '批次', dataIndex: 'batch', key: 'batch', render: (b: string | null) => b ?? '-' },
    { title: '兑换用户', dataIndex: 'usedByUserId', key: 'usedByUserId', render: (u: string | null) => u ?? '-' },
    { title: '兑换时间', dataIndex: 'usedAt', key: 'usedAt', render: (t: number | null) => formatTs(t) },
    { title: '有效期', dataIndex: 'expireAt', key: 'expireAt', render: (t: number | null) => (t ? formatTs(t) : '永久') },
    { title: '生成时间', dataIndex: 'createdAt', key: 'createdAt', render: (t: number | null) => formatTs(t) },
  ];

  const codesText = useMemo(() => (resultCodes ?? []).join('\n'), [resultCodes]);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card title="批量生码">
        <Alert
          type="info" showIcon style={{ marginBottom: 16 }}
          message="每个兑换码一人一次，用户在小程序「我的」页输入即得一张免费成书券（解锁电子版 + 付印版 PDF 导出，不含实体打印）。"
        />
        <PermissionGate
          point={PERMISSIONS.VOUCHER_GRANT}
          fallback={<Alert type="warning" showIcon message="无生码权限（需 voucher:grant）" />}
        >
          <Form form={genForm} layout="inline" initialValues={{ count: 30 }}>
            <Form.Item label="数量" name="count" rules={[{ required: true, type: 'number', min: 1, max: 500 }]}>
              <InputNumber min={1} max={500} />
            </Form.Item>
            <Form.Item label="批次" name="batch" tooltip="渠道归因，如 xhs-0712 / douyin-0712 / shipinhao-0712">
              <Input placeholder="如 xhs-0712" allowClear style={{ width: 170 }} />
            </Form.Item>
            <Form.Item label="备注" name="note">
              <Input placeholder="可选" allowClear style={{ width: 150 }} />
            </Form.Item>
            <Form.Item label="有效期" name="expireAt" tooltip="不填 = 永久有效">
              <DatePicker showTime placeholder="永久" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" loading={genMutation.isPending} onClick={handleGenerate}>
                生成
              </Button>
            </Form.Item>
          </Form>
        </PermissionGate>
      </Card>

      <Card title="渠道漏斗（按批次）">
        <Alert
          type="info" showIcon style={{ marginBottom: 12 }}
          message="核销后的行为只统计核销时间之后发生的；讲述分钟按录音时长汇总；已付款仅含现有商品（69 元 / 499 商品上线后再拆分）。批次填 internal-xxx 即标为内部测试。"
        />
        <Table
          rowKey={(r) => r.batch ?? '__none__'}
          loading={funnelQuery.isLoading}
          dataSource={funnelQuery.data?.rows ?? []}
          columns={funnelColumns}
          pagination={false}
          size="small"
        />
      </Card>

      <Card
        title="兑换码列表"
        extra={
          <Form
            form={filterForm}
            layout="inline"
            onFinish={(v) => setFilters({ status: v.status, batch: v.batch?.trim() || undefined })}
          >
            <Form.Item name="status">
              <Select
                allowClear placeholder="状态" style={{ width: 120 }}
                options={[{ value: 'unused', label: '未使用' }, { value: 'used', label: '已使用' }]}
              />
            </Form.Item>
            <Form.Item name="batch">
              <Input allowClear placeholder="批次" style={{ width: 140 }} />
            </Form.Item>
            <Form.Item>
              <Button htmlType="submit">筛选</Button>
            </Form.Item>
          </Form>
        }
      >
        <Table
          rowKey="code"
          loading={listQuery.isLoading}
          dataSource={listQuery.data?.items ?? []}
          columns={columns}
          pagination={{ pageSize: 20, showTotal: (t) => `共 ${t} 个` }}
          size="small"
        />
      </Card>

      <Modal
        open={resultCodes != null}
        title={`已生成 ${resultCodes?.length ?? 0} 个兑换码`}
        onCancel={() => setResultCodes(null)}
        footer={[
          <Button
            key="copy" type="primary"
            onClick={() => {
              void navigator.clipboard?.writeText(codesText);
              message.success('已复制到剪贴板');
            }}
          >
            复制全部
          </Button>,
          <Button key="close" onClick={() => setResultCodes(null)}>关闭</Button>,
        ]}
      >
        <Alert
          type="success" showIcon style={{ marginBottom: 12 }}
          message="复制下方兑换码去小红书 / 视频号发布。建议不同渠道用不同批次，便于回看各渠道核销。"
        />
        <Input.TextArea value={codesText} readOnly autoSize={{ minRows: 6, maxRows: 16 }} />
      </Modal>
    </Space>
  );
}

export default function RedeemCodesPage() {
  return (
    <PermissionGate
      point={PERMISSIONS.REDEEM_CODE_READ}
      fallback={<Alert type="warning" showIcon message="无权访问兑换码（需 redeem_code:read）" />}
    >
      <RedeemCodesPageInner />
    </PermissionGate>
  );
}
